import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  Card,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Button,
  Divider,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// shared
import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import AddEntityButton from 'src/components/shared/AddEntityButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import EditButton from 'src/components/shared/EditButton';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';

// services / ctx
import api from 'src/services/BaseService';
import './muhaberatOutput.css';
import AddStoreModal from 'src/components/modals/AddStoreModal';
import CustomFilter from 'src/components/filter/CustomFilter';
import { useLocalization } from 'src/context/LocalizationContext';
import { toast } from 'react-toastify';

const mallID = localStorage.getItem('mallID');

export default function StoreList() {
  const { language } = useLocalization();
  const navigate = useNavigate();

  const [storeList, setStoreList] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [title, setTitle] = useState('');
  const [mallTitle, setMallTitle] = useState('');
  const [addEditStoreOpen, setAddEditStoreOpen] = useState(false);
  const [isModalEdit, setIsModalEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [isPrimary, setIsPrimary] = useState('');
  const [mallList, setMallList] = useState([]);
  const [filterModal, setFilterModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStoreToDelete, setSelectedStoreToDelete] = useState('');
  const [showPopUpError, setShowPopUpError] = useState(false);

  const isMobile = useMediaQuery('(max-width:768px)');

  // ---- Kurumsal cache (tek sefer çek) ----
  const [corpLoading, setCorpLoading] = useState(false);
  const [corpLoaded, setCorpLoaded] = useState(false);
  const [corpTitles, setCorpTitles] = useState([]); // Autocomplete options
  const [corpMap, setCorpMap] = useState(new Map()); // title -> corpObj (billing vs.)

  const fetchCorporationsOnce = async () => {
    if (corpLoaded || corpLoading) return; // zaten var / çekiliyor
    try {
      setCorpLoading(true);
      const res = await api.post('flow/get-corporations');
      const raw = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.result || res?.data?.rows || [];

      const map = new Map();
      raw.forEach((corp) => {
        const title = String(corp?.title || '').trim();
        if (!title) return;
        map.set(title, {
          title,
          companyCorporationalCode: String(
            corp?.companyCorporationalCode || ''
          ),
          billing: {
            taxNo: String(corp?.billing?.taxNo || ''),
            taxAdministration: String(corp?.billing?.taxAdministration || ''),
            address: String(corp?.billing?.address || ''),
            region: String(corp?.billing?.region || ''),
            city: String(corp?.billing?.city || ''),
            country: String(corp?.billing?.country || 'Türkiye'),
            email: String(corp?.billing?.email || ''),
            neighborhood: String(corp?.billing?.neighborhood || ''),
          },
        });
      });

      setCorpMap(map);
      setCorpTitles(Array.from(map.keys()));
      setCorpLoaded(true);
    } catch (e) {
      console.error('flow/get-corporations error:', e);
      toast.error('Kurumsal listesi alınamadı.');
      setCorpMap(new Map());
      setCorpTitles([]);
      setCorpLoaded(false);
    } finally {
      setCorpLoading(false);
    }
  };

  // ---- Stores ----
  function getStores(malls) {
    api
      .post('flow/get-malls-branches', { mallID })
      .then((response) => {
        if (response.data) {
          let mallTitleAddedArray = [];
          let allStores = response.data.result;
          for (let i = 0; i < allStores?.length; i++) {
            const selectedMallTitle = malls.find(
              (item) => item.id === allStores[i].mallID
            ).title;
            mallTitleAddedArray = allStores.map((obj) => {
              return { ...obj, mallTitle: selectedMallTitle };
            });
          }
          setStoreList(mallTitleAddedArray);
          setOriginalData(mallTitleAddedArray);
        }
      })
      .catch(() => {});
  }

  function getMallsAndStores() {
    api
      .post('/malls/get-malls', { withOffices: true })
      .then((response) => {
        if (response.data) {
          getStores(response.data.result);
          setMallList(response.data.result);
        }
      })
      .catch(() => {});
  }

  useEffect(() => {
    getMallsAndStores();
  }, []);

  // ---- Filters ----
  const handleModal = () => setFilterModal(true);
  const handleCloseModal = () => setFilterModal(false);

  const getFilteredStoreList = () => {
    let filteredStoreList;
    if (
      title ||
      mallTitle ||
      (startDate && endDate) ||
      isPrimary ||
      responsiblePerson
    ) {
      filteredStoreList = originalData.filter((item) => {
        const titleMatch = title
          ? item.title.toLowerCase().includes(title.toLowerCase())
          : true;
        const mallTitleMatch = mallTitle
          ? item.mallTitle.toLowerCase().includes(mallTitle.toLowerCase())
          : true;
        const responsiblePersonMatch = responsiblePerson
          ? item.locations[0].userNameLastname
            ? item.locations[0].userNameLastname
                .toLowerCase()
                .includes(responsiblePerson.toLowerCase())
            : null
          : true;
        const dateMatch =
          startDate && endDate
            ? moment.unix(item.createdDateTime).format('DD-MM-YYYY') >=
                moment(startDate).format('DD-MM-YYYY') &&
              moment.unix(item.createdDateTime).format('DD-MM-YYYY') <=
                moment(endDate).format('DD-MM-YYYY')
            : true;
        const isPrimaryMatch = isPrimary ? item.isPrimary === isPrimary : true;
        return (
          titleMatch &&
          mallTitleMatch &&
          dateMatch &&
          isPrimaryMatch &&
          responsiblePersonMatch
        );
      });
    } else {
      filteredStoreList = originalData;
    }
    setStoreList(filteredStoreList);
  };

  const clearFilters = () => {
    setStoreList(originalData);
    setTitle('');
    setMallTitle('');
    setStartDate(null);
    setEndDate(null);
    setIsPrimary('');
    setResponsiblePerson('');
  };

  // ---- Delete ----
  const STORE_TABLE_HEAD = useMemo(
    () => [
      {
        id: 'title',
        label: language.commons.column.store_name,
        alignRight: false,
        sx: { width: 100 },
      },
      {
        id: 'mallTitle',
        label: language.commons.column.mall_plaza,
        alignRight: false,
        sx: { width: 100 },
      },
      {
        id: 'userNameLastname',
        label: language.commons.column.responsible_person,
        alignRight: false,
        sortBy: 'locations[0].userNameLastname',
        format: (value, row) =>
          row.locations[0].userNameLastname
            ? row.locations[0].userNameLastname
            : '-',
        sx: { width: 100 },
      },
      {
        id: 'createdDateTime',
        label: language.commons.column.created_date,
        alignRight: false,
        format: (value) => moment.unix(value).format('DD/MM/YYYY HH:mm'),
        sx: { width: 100 },
      },
      {
        id: 'isPrimary',
        label: language.commons.column.priority_status,
        alignRight: false,
        format: (value) => (value === '1' ? 'Evet' : 'Hayır'),
        sx: { width: 50 },
      },
      {
        id: 'operation',
        label: '',
        alignRight: false,
        format: (value, row) => (
          <EditButton to={`/dashboard/edit-store/${row.id}/${row.mallID}`} />
        ),
        sx: { width: 25 },
      },
      {
        id: 'status',
        label: '',
        alignRight: false,
        format: (value, row) => (
          <Button
            onClick={() => {
              setShowDeleteModal(true);
              setSelectedStoreToDelete(row);
            }}
          >
            Kaldır
          </Button>
        ),
        sx: { width: 25 },
      },
    ],
    [language]
  );

  const handleDeleteStore = () => {
    api
      .post('flow/close-branch', {
        branchID: selectedStoreToDelete.branchID,
      })
      .then((response) => {
        if (response.data.status) {
          toast.success('Mağaza başarıyla kaldırıldı.');
          getMallsAndStores();
        } else {
          toast.error('Mağaza kaldırılırken bir hata meydana geldi.');
        }
      })
      .finally(() => {
        setShowDeleteModal(false);
        setSelectedStoreToDelete('');
      });
  };

  // ---- Add Modal open/close ----
  const handleOpenAddStoreModal = async () => {
    setIsModalEdit(false);
    setAddEditStoreOpen(true);
    // kurumsallar yoksa çek (listeyi etkilemeden)
    if (!corpLoaded && !corpLoading) {
      fetchCorporationsOnce();
    }
  };

  const handleCloseAddStoreModal = () => {
    setAddEditStoreOpen(false);
    // listeyi tazele
    getMallsAndStores();
  };

  // ---- Export ----
  function exportToExcel() {
    let temp1 = storeList.map((x, index) => {
      return {
        id: index + 1,
        title: x?.title ? x?.title : '-',
        country: x?.billing?.country ? x?.billing?.country : '-',
        city: x?.billing?.city ? x?.billing?.city : '-',
        region: x?.billing?.region ? x?.billing?.region : '-',
        taxAdministration: x?.billing?.taxAdministration
          ? x?.billing?.taxAdministration
          : '-',
        taxNo: x?.billing?.taxNo ? x?.billing?.taxNo : '-',
        companyCorporationalCode: x?.companyCorporationalCode
          ? x?.companyCorporationalCode
          : '-',
        companyLegalTitle: x?.companyLegalTitle ? x?.companyLegalTitle : '-',
        branchID: x?.branchID ? x?.branchID : '-',
        createdDateTime: moment(x?.createdDateTime * 1000).format('DD/MM/YYYY'),
      };
    });

    api
      .post('excel/export', {
        type: 'excel',
        content: temp1,
        header: {
          No: 'number',
          'Mağaza Adı': 'string',
          Ülke: 'string',
          Şehir: 'string',
          Bölge: 'string',
          'Vergi Dairesi': 'string',
          'Vergi No': 'string',
          'Şirket Kodu': 'string',
          'Ticari Ünvan': 'string',
          'Branch ID': 'number',
          'Oluşturma Tarihi': 'string',
        },
        sheetTitle: 'Mağazalar',
      })
      .then((response) => {
        if (response.data.status) {
          try {
            const newWindow = window.open(response.data.result, '_blank');
            if (
              !newWindow ||
              newWindow.closed ||
              typeof newWindow.closed == 'undefined'
            ) {
              setShowPopUpError(true);
              throw new Error(
                'Pop-up engellendi. Lütfen pop-up engelleyicisini devre dışı bırakın.'
              );
            }
          } catch (e) {
            setShowPopUpError(true);
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  }

  return (
    <>
      <Helmet>
        <title> {language.store_list.titles.site_title} </title>
      </Helmet>

      <Stack style={{ paddingInline: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {language.store_list.titles.title}
          </Typography>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AddEntityButton
              label="Mağaza Ekle"
              onClick={handleOpenAddStoreModal}
            />
            <ExportToExcelButton
              setShowPopUpError={setShowPopUpError}
              showPopUpError={showPopUpError}
              onClick={exportToExcel}
            />
          </div>
        </Stack>

        <Stack
          direction="row"
          justifyContent={isMobile ? 'space-between' : 'end'}
          mb={5}
        >
          {isMobile ? (
            <FilterViewButton variant="contained" onClick={handleModal} />
          ) : null}
        </Stack>

        <Card>
          <CustomFilter
            modalType="drawer"
            filterModal={filterModal}
            handleCloseModal={() => setFilterModal(false)}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="space-between"
              py={2}
              px={1}
              spacing={2}
            >
              <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                <TextField
                  sx={{ flex: 1 }}
                  label={language.commons.column.store_name}
                  variant="outlined"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </FormControl>

              <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                <TextField
                  sx={{ flex: 1 }}
                  label={language.commons.column.mall_plaza}
                  variant="outlined"
                  value={mallTitle}
                  onChange={(e) => setMallTitle(e.target.value)}
                />
              </FormControl>

              <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                <TextField
                  sx={{ flex: 1 }}
                  label={language.commons.column.responsible_person}
                  variant="outlined"
                  value={responsiblePerson}
                  onChange={(e) => setResponsiblePerson(e.target.value)}
                />
              </FormControl>

              <FormControl
                sx={{ flex: 1, minWidth: '100%' }}
                variant="outlined"
              >
                <InputLabel id="priority-store-label">
                  {language.commons.column.priority_status}
                </InputLabel>
                <Select
                  labelId="priority-store-label"
                  value={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.value)}
                  label={language.commons.column.priority_status}
                >
                  <MenuItem value="1">{language.commons.yes}</MenuItem>
                  <MenuItem value="0">{language.commons.no}</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-start"
              py={2}
              px={1}
              spacing={2}
            >
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  format="DD/MM/YYYY"
                  label={language.commons.labels.start_date}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  format="DD/MM/YYYY"
                  label={language.commons.labels.end_date}
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                />
              </LocalizationProvider>
            </Stack>

            <Divider sx={{ width: '100%' }} />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              py={2}
              px={1}
              spacing={2}
            >
              <ClearButton onClick={clearFilters} />
              <FilterButton onClick={getFilteredStoreList} />
            </Stack>
          </CustomFilter>

          <DynamicTable
            data={storeList}
            headers={STORE_TABLE_HEAD}
            initialOrder="createdDateTime"
            initalOrderDirection="asc"
            isOrdered
            isMobile={isMobile}
            handleModal={handleModal}
            handleCloseModal={handleCloseModal}
            rowOnClick={(row) =>
              navigate(`/dashboard/edit-store/${row.id}/${row.mallID}`)
            }
          />
        </Card>
      </Stack>

      <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle>Silme İşlemi</DialogTitle>
        <DialogContent>
          {selectedStoreToDelete && (
            <p>
              <b>{selectedStoreToDelete.title}</b> adlı mağazayı silmek
              istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)} color="primary">
            Hayır
          </Button>
          <Button onClick={handleDeleteStore} color="secondary">
            Evet
          </Button>
        </DialogActions>
      </Dialog>

      {addEditStoreOpen && (
        <AddStoreModal
          open={addEditStoreOpen}
          onClose={handleCloseAddStoreModal}
          isEdit={isModalEdit}
          editData={editData}
          mallList={mallList}
          // kurumsal cache/flags
          corpLoading={corpLoading && !corpLoaded}
          corpTitles={corpTitles}
          corpMap={corpMap}
        />
      )}
    </>
  );
}
